// File: src/pages/TestPage.jsx
import { useMemo, useEffect, useRef, useState } from "react";
import { fetchTestById, storeUserResult } from "../api";
import { useParams, useNavigate } from "react-router-dom";
import useImagePreloader from "../hooks/useImagePreloader";
import { MdBookmarkAdd } from "react-icons/md";
import EditBookmarkModal from "../components/EditBookmarkModal";

/** Helper */
function formatMs(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const ss = (totalSeconds % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}

export default function TestPage({ user }) {
  const { testId } = useParams();
  const navigate = useNavigate();

  const uid = user?.uid || null;
  const userEmail = user?.email || null;
  const storageKey = uid ? `qgriller_inprogress_${testId}_${uid}` : null;

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);

  const [accessAllowed, setAccessAllowed] = useState(false);
  const [accessDeniedReason, setAccessDeniedReason] = useState("");

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [startTime, setStartTime] = useState(null);
  const [remainingMs, setRemainingMs] = useState(null);
  const [submittedResult, setSubmittedResult] = useState(null);

  const [showBookmarkModal, setShowBookmarkModal] = useState(false);

  const autosaveRef = useRef(null);
  const timerRef = useRef(null);

  const allImageUrls = useMemo(() => {
    if (!test || !test.questions) return [];
    const urls = [];
    test.questions.forEach((question) => {
      if (question.questionImage) urls.push(question.questionImage);
      if (question.isSolutionAvailable && question.solutionImage)
        urls.push(question.solutionImage);
    });
    // dedupe
    return Array.from(new Set(urls));
  }, [test]);

  // call the hook (starts background loading)
  const { statusMap, prioritize, isAllLoaded } = useImagePreloader(
    allImageUrls,
    { concurrency: 4 }
  );

  // When the user jumps to a specific question (or currentIndex changes) prioritize its images
  useEffect(() => {
    if (!test || !test.questions) return;
    const q = test.questions[currentIndex];
    if (!q) return;
    const toPrioritize = [];
    if (q.questionImage) toPrioritize.push(q.questionImage);
    if (q.solutionImage) toPrioritize.push(q.solutionImage);
    prioritize(toPrioritize);
  }, [currentIndex, test, prioritize]);

  // Optionally highlight loading state on the question image:
  const curQ = test?.questions[currentIndex];
  const curImgStatus = curQ ? statusMap[curQ.questionImage] : null;

  // Fetch test doc once on mount / testId change
  useEffect(() => {
    let mounted = true;
    (async () => {
      const t = await fetchTestById(testId);
      if (!mounted) return;
      if (!t) {
        alert("Test not found");
        navigate("/");
        return;
      }
      setTest(t);
      setLoading(false);

      // If user is logged in and has existing result -> show analysis
      if (uid) {
        const existingResult = (t.results || {})[uid] || null;
        if (existingResult) {
          setSubmittedResult(existingResult);
          try {
            localStorage.removeItem(`qgriller_inprogress_${testId}_${uid}`);
          } catch (e) {}
          // No need to init timer/progress
          return;
        }
      }

      // If user is logged in, attempt to initialize timer/progress only if they have access.
      // We'll compute access below in the effect that watches `test` and `user`.
    })();
    return () => (mounted = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // Re-check access and restore (or prevent) progress when test or user changes.
  useEffect(() => {
    if (!test) return;

    // If there's a stored submission for this uid -> allow analysis view regardless of access
    if (uid && (test.results || {})[uid]) {
      setSubmittedResult((test.results || {})[uid]);
      setAccessAllowed(true); // not important but safe
      try {
        localStorage.removeItem(`qgriller_inprogress_${testId}_${uid}`);
      } catch (e) {}
      return;
    }

    // Determine access:
    // allowed if owner OR public OR sharedWith contains uid or email
    const isOwner = uid && test.ownerUid && uid === test.ownerUid;
    const isPublic = !!test.isPublic;
    const shared = Array.isArray(test.sharedWith) ? test.sharedWith : [];
    const isShared =
      uid &&
      (shared.includes(uid) || (userEmail && shared.includes(userEmail)));

    const allowed = !!(isOwner || isPublic || isShared);
    setAccessAllowed(allowed);

    if (!allowed) {
      setAccessDeniedReason(
        isPublic ? "" : "You do not have permission to take this private test."
      );
      // clear any start/progress that might have been set
      setStartTime(null);
      setAnswers({});
      setRemainingMs(null);
      return;
    }

    // If allowed and user present, initialize / restore local progress
    if (allowed && uid) {
      const timeLimitMins = test.timeLimitMins || 60;
      const msLimit = timeLimitMins * 60 * 1000;
      const saved = storageKey ? localStorage.getItem(storageKey) : null;
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.startTime) {
            setStartTime(parsed.startTime);
            const elapsed = Date.now() - parsed.startTime;
            setRemainingMs(Math.max(0, msLimit - elapsed));
          } else {
            setStartTime(Date.now());
            setRemainingMs(msLimit);
          }
          if (parsed.answers) setAnswers(parsed.answers);
        } catch (e) {
          setStartTime(Date.now());
          setRemainingMs(msLimit);
        }
      } else {
        setStartTime(Date.now());
        setRemainingMs(msLimit);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test, uid, userEmail]);

  // Timer effect: only runs if a startTime exists and test not submitted
  useEffect(() => {
    if (!startTime || submittedResult) return;
    const tick = () => {
      const totalMs = (test?.timeLimitMins || 60) * 60 * 1000;
      const elapsed = Date.now() - startTime;
      const rem = Math.max(0, totalMs - elapsed);
      setRemainingMs(rem);
      if (rem <= 0) {
        handleAutoSubmit();
      }
    };
    timerRef.current = setInterval(tick, 500);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, test, submittedResult]);

  // Autosave (only when a user is present and we have storageKey and accessAllowed)
  useEffect(() => {
    if (!storageKey || !accessAllowed) return;
    autosaveRef.current = setInterval(() => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ startTime, answers, savedAt: Date.now() })
        );
      } catch (e) {}
    }, 10000);
    return () => clearInterval(autosaveRef.current);
  }, [storageKey, startTime, answers, accessAllowed]);

  // scroll the current palette item into view (nearest keeps it within container)
  useEffect(() => {
    const el = document.getElementById(`q-${currentIndex}`);
    el?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [currentIndex]);
  let total = test?.questions?.length;
  // shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight") {
        setCurrentIndex((i) => Math.min(i + 1, total - 1));
      }
      if (e.key === "ArrowLeft") {
        setCurrentIndex((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [total]);

  // Helpers
  function updateAnswerForIndex(index, payload) {
    if (!uid || !accessAllowed) return;
    setAnswers((prev) => {
      const next = { ...prev };
      const prevFor = next[index] || {
        value: null,
        marked: false,
        saved: false,
        timeSpentMs: 0,
      };
      next[index] = { ...prevFor, ...payload, saved: true };
      try {
        if (storageKey)
          localStorage.setItem(
            storageKey,
            JSON.stringify({ startTime, answers: next })
          );
      } catch (e) {}
      return next;
    });
  }

  function handleSelectOption(index, option) {
    if (!uid || !accessAllowed) return;
    const question = test.questions[index];
    if (!question) return;
    if (question.questionType === "MMCQ") {
      const cur = (answers[index]?.value || "").toString();
      let set = new Set(cur.split("").filter(Boolean));
      if (set.has(option)) set.delete(option);
      else set.add(option);
      const val = Array.from(set).sort().join("") || null;
      updateAnswerForIndex(index, { value: val });
    } else {
      const cur = (answers[index]?.value || "").toString();
      updateAnswerForIndex(index, { value: cur === option ? null : option });
    }
  }

  function handleNumericChange(index, val) {
    if (!uid || !accessAllowed) return;
    updateAnswerForIndex(index, { value: val });
  }

  function handleToggleMark(index) {
    if (!uid || !accessAllowed) return;
    setAnswers((prev) => {
      const next = { ...prev };
      const prevFor = next[index] || {
        value: null,
        marked: false,
        saved: false,
        timeSpentMs: 0,
      };
      next[index] = { ...prevFor, marked: !prevFor.marked };
      try {
        if (storageKey)
          localStorage.setItem(
            storageKey,
            JSON.stringify({ startTime, answers: next })
          );
      } catch (e) {}
      return next;
    });
  }

  function getStatusForIndex(index) {
    const a = answers[index];
    if (!a) return "notAttempted";
    if (a.marked && a.value) return "markedSaved";
    if (a.marked) return "marked";
    if (!a.value) return "notAttempted";
    if (a.saved) return "saved";
    return "notAttempted";
  }

  function checkCorrect(correctKey, qType, given) {
    if (correctKey == null) return false;
    if (qType === "SMCQ") {
      return String(given) === String(correctKey);
    } else if (qType === "MMCQ") {
      const a = (given || "").toString().split("").sort().join("");
      const b = (correctKey || "").toString().split("").sort().join("");
      return a === b && a !== "";
    } else if (qType === "NUMERICAL") {
      const g = parseFloat(String(given || "").trim());
      const c = parseFloat(String(correctKey || "").trim());
      if (Number.isFinite(g) && Number.isFinite(c)) {
        const tol = Math.abs(c) * 0.01 + 0.001;
        return Math.abs(g - c) <= tol;
      }
      return false;
    } else {
      return String(given) === String(correctKey);
    }
  }

  // keep a ref to the latest answers so autosubmit can read current answers [to prevent stale closure issue with autosubmit]
  const latestAnswersRef = useRef(answers);
  useEffect(() => {
    latestAnswersRef.current = answers;
  }, [answers]);

  // Submit (requires user & access)
  async function handleSubmit(final = true) {
    if (!test) return;
    if (!uid) {
      alert("Please log in to submit the test.");
      return;
    }
    if (!accessAllowed) {
      alert("You don't have permission to submit this test.");
      return;
    }

    const currentAnswers = latestAnswersRef.current;

    const questionDetails = test.questions.map((q, idx) => {
      const userAns = currentAnswers[idx]?.value ?? null;
      const attempted =
        userAns !== null && userAns !== "" && userAns !== undefined;
      const correct = checkCorrect(q.answerKey, q.questionType, userAns);
      let marks = 0;
      if (!attempted) marks = 0;
      else marks = correct ? 4 : -1;
      return {
        index: idx,
        chapter: q.chapter,
        assignment: q.assignment,
        qno: q.qno,
        questionType: q.questionType,
        answerKey: q.answerKey,
        userAnswer: userAns,
        attempted,
        correct,
        marks,
      };
    });
    const totalMarks = questionDetails.reduce((s, d) => s + d.marks, 0);
    const timeTakenMs = Date.now() - (startTime || Date.now());
    const resultObj = {
      marks: totalMarks,
      questionDetails,
      timeTakenMs,
    };

    try {
      await storeUserResult(testId, uid, resultObj);
      setSubmittedResult(resultObj);
      try {
        if (storageKey) localStorage.removeItem(storageKey);
      } catch (e) {}
      alert("Submitted! Your score: " + totalMarks);
    } catch (e) {
      console.error("Submit failed", e);
      alert("Submit failed: " + (e.message || e));
    }
  }

  async function handleAutoSubmit() {
    if (submittedResult) return;
    await handleSubmit(true);
  }

  // Render states
  if (loading || !test) {
    return <div className="p-6">Loading test...</div>;
  }

  // Not logged in -> require login (prevents anonymous)
  if (!uid) {
    return (
      <div className="p-6 flex flex-col items-center gap-4">
        <h1 className="text-2xl font-bold">{test.title || "Test"}</h1>
        <div className="text-sm text-gray-600">
          Questions: {test.questions.length}
        </div>
        <div className="max-w-xl text-center mt-4">
          <p className="mb-2">You must be logged in to take this test.</p>
          <p className="mb-4 text-sm text-gray-500">
            Logging in ensures your result is saved to your account and prevents
            misuse.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate("/login")}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Log in
            </button>
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2 border rounded"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If user is logged in but doesn't have access and has no submission -> deny attending
  if (!accessAllowed && !submittedResult) {
    return (
      <div className="p-6 max-w-xl mx-auto text-center">
        <h1 className="text-2xl font-bold mb-2">Access denied</h1>
        <p className="mb-4 text-gray-700">
          You do not have permission to take this test.
        </p>
        <p className="mb-4 text-sm text-gray-500">
          This test is private. Only the owner or users listed in the shared
          list can take it.
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 border rounded"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  // If submitted -> analysis UI
  if (submittedResult) {
    const details = submittedResult.questionDetails;
    const qCount = test.questions.length;
    const currentQuestion = test.questions[currentIndex];

    return (
      <div className="px-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">{test.title || "Test"}</h1>
          {!isAllLoaded && <span className="text-sm">Loading...</span>}
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <div className="border rounded p-3">
              <div className="mb-2 flex justify-between">
                <div>
                  Question {currentIndex + 1} / {qCount}
                </div>
                <div>
                  <MdBookmarkAdd
                    className="text-xl text-green-600 cursor-pointer "
                    onClick={() => setShowBookmarkModal(true)}
                  />
                </div>
              </div>
              <div className="mb-4">
                <img
                  src={currentQuestion.questionImage}
                  alt={`Q ${currentIndex + 1}`}
                  className="max-h-[480px] w-full object-contain border"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {currentQuestion.questionType === "NUMERICAL" ? (
                  <div>
                    <div className="mb-1 text-sm">Numeric answer</div>
                    <input
                      value={details[currentIndex]?.userAnswer ?? ""}
                      readOnly
                      className="border w-48 px-3 py-2 rounded"
                    />
                    <div className="mt-2">
                      <div className="text-sm">
                        Correct answer: {currentQuestion.answerKey}
                      </div>
                      <div className="text-sm">
                        {details[currentIndex]?.correct ? (
                          <span className="text-green-700">
                            You were correct
                          </span>
                        ) : (
                          <span className="text-red-700">Incorrect</span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  ["A", "B", "C", "D"].map((opt) => {
                    const user = details[currentIndex]?.userAnswer;
                    const correctKey = currentQuestion.answerKey;
                    const isUser =
                      user &&
                      (currentQuestion.questionType === "MMCQ"
                        ? user.includes(opt)
                        : user === opt);
                    const isCorrect =
                      correctKey &&
                      (currentQuestion.questionType === "MMCQ"
                        ? correctKey.includes(opt)
                        : correctKey === opt);
                    const base =
                      "p-3 border rounded flex items-center justify-between";
                    const classes = [
                      base,
                      isCorrect ? "border-green-600 bg-green-100" : "",
                      isUser && !isCorrect ? "border-red-600 bg-red-100" : "",
                    ].join(" ");
                    return (
                      <div key={opt} className={classes}>
                        <div className="font-medium">{opt}</div>
                        <div>
                          {isUser && (
                            <span className="text-sm">Your choice</span>
                          )}
                          {isCorrect && (
                            <span className="ml-2 text-sm font-semibold">
                              Correct
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {currentQuestion.isSolutionAvailable &&
                currentQuestion.solutionImage && (
                  <div className="mt-4">
                    <div className="text-sm mb-2 font-medium">Solution:</div>
                    <img
                      src={currentQuestion.solutionImage}
                      alt="Solution"
                      className="w-full object-contain border"
                    />
                  </div>
                )}

              {showBookmarkModal && (
                <EditBookmarkModal
                  user={user}
                  question={{
                    chapter: currentQuestion.chapter,
                    assignment: currentQuestion.assignment,
                    qno: currentQuestion.qno,
                    questionImage: currentQuestion.questionImage,
                    answerKey: currentQuestion.answerKey,
                    questionType: currentQuestion.questionType,
                    answerKeyCandidates: currentQuestion.answerKeyCandidates,
                    isSolutionAvailable: currentQuestion.isSolutionAvailable,
                    solutionImage: currentQuestion.solutionImage,
                    // optional: include testId if you want a reference
                    testId,
                  }}
                  onClose={() => setShowBookmarkModal(false)}
                  onSaved={(saved) => {
                    // optional: show toast or update UI
                    console.log("Bookmark saved", saved);
                  }}
                />
              )}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                className="px-3 py-2 border rounded"
              >
                Prev
              </button>
              <button
                onClick={() =>
                  setCurrentIndex((i) => Math.min(qCount - 1, i + 1))
                }
                className="px-3 py-2 border rounded"
              >
                Next
              </button>
            </div>
          </div>

          <div className="w-64 sm:w-32 md:w-36 lg:w-64 xl:w-64">
            <div className="mb-3 ">
              <div className="text-sm font-medium mb-2">Question palette</div>
              <div className="grid lg:grid-cols-5 md:grid-cols-3 sm:grid-cols-2 gap-2 mt-2 overflow-auto max-h-80 border rounded p-2">
                {test.questions.map((q, idx) => {
                  const st = (submittedResult.questionDetails || [])[idx];
                  const cls = st?.correct
                    ? "bg-green-400"
                    : st?.attempted
                    ? "bg-red-400"
                    : "bg-gray-200";
                  return (
                    <div
                      key={idx}
                      id={`q-${idx}`}
                      onClick={() => setCurrentIndex(idx)}
                      className={`cursor-pointer p-2 text-center rounded ${cls} transition ${
                        currentIndex === idx
                          ? "ring-2 ring-offset-2 ring-blue-500"
                          : "bg-gray-100 "
                      }`}
                    >
                      {idx + 1}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-2">Summary</div>
              <div>Total Qs: {test.questions.length}</div>
              <div>Score: {submittedResult.marks}</div>

              <div>Time taken: {formatMs(submittedResult.timeTakenMs)}</div>
              <div className="text-sm text-gray-500">Submitted</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Interactive test UI (user logged in and accessAllowed)
  const qCount = test.questions.length;
  const currentQuestion = test.questions[currentIndex];
  const statusColor = {
    saved: "bg-green-400",
    marked: "bg-yellow-400",
    markedSaved: "bg-indigo-400",
    notAttempted: "bg-gray-200",
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="text-2xl font-bold">{test.title || "Test"}</h1>

          {!isAllLoaded && <span className="text-sm">Loading...</span>}
          <div className="text-sm text-gray-600">Questions: {qCount}</div>
        </div>
        <div className="text-right">
          <div className="mb-1">Time left</div>
          <div className="text-lg font-mono">{formatMs(remainingMs || 0)}</div>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <div className="border rounded p-3">
            <div className="mb-2 flex justify-between items-center">
              <div>
                Question {currentIndex + 1} / {qCount}
              </div>
              <div>
                <button
                  onClick={() => handleToggleMark(currentIndex)}
                  className={`px-2 py-1 text-sm rounded border ${
                    answers[currentIndex]?.marked ? "bg-yellow-200" : ""
                  }`}
                >
                  {answers[currentIndex]?.marked ? "Marked" : "Mark"}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <img
                src={currentQuestion.questionImage}
                alt={`Q ${currentIndex + 1}`}
                className="max-h-[520px] w-full object-contain border"
              />
            </div>

            {currentQuestion.questionType === "NUMERICAL" ? (
              <div>
                <div className="mb-2">Enter numeric answer</div>
                <input
                  type="text"
                  value={answers[currentIndex]?.value ?? ""}
                  onChange={(e) =>
                    handleNumericChange(currentIndex, e.target.value)
                  }
                  className="border px-3 py-2 rounded w-48"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {["A", "B", "C", "D"].map((opt) => {
                  const chosen = (
                    answers[currentIndex]?.value || ""
                  ).toString();
                  const isSelected =
                    currentQuestion.questionType === "MMCQ"
                      ? chosen.includes(opt)
                      : chosen === opt;
                  return (
                    <button
                      key={opt}
                      onClick={() => handleSelectOption(currentIndex, opt)}
                      className={`p-3 rounded flex justify-between items-center  ${
                        isSelected ? "bg-green-100" : "bg-zinc-100"
                      } ${
                        isSelected ? "hover:bg-green-200" : "hover:bg-slate-100"
                      } active:scale-[.99] border shadow-sm`}
                    >
                      <div className="font-medium">{opt}</div>
                      <div className="text-sm">
                        {isSelected ? "Selected" : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                className="px-3 py-2 border rounded"
              >
                Prev
              </button>
              <button
                onClick={() =>
                  setCurrentIndex((i) => Math.min(qCount - 1, i + 1))
                }
                className="px-3 py-2 border rounded"
              >
                Next
              </button>

              <button
                onClick={() => {
                  if (!window.confirm("Submit test now?")) return;
                  handleSubmit(true);
                }}
                className="ml-auto px-4 py-2 bg-green-600 text-white rounded"
              >
                Submit Now
              </button>
            </div>
          </div>
        </div>

        {/* Question Palette */}
        <div className="w-64 sm:w-32 md:w-36 lg:w-64 xl:w-64">
          <div className="mb-3 ">
            <div className="text-sm font-medium mb-2">Question Palette</div>
            <div className="grid lg:grid-cols-5 md:grid-cols-3 sm:grid-cols-2 gap-2 mt-2 overflow-auto max-h-80 border rounded p-2">
              {test.questions.map((q, idx) => {
                const status = getStatusForIndex(idx);
                const cls = statusColor[status] || "bg-gray-200";
                return (
                  <div
                    key={idx}
                    id={`q-${idx}`}
                    onClick={() => setCurrentIndex(idx)}
                    className={`cursor-pointer p-2 text-center rounded ${cls} transition ${
                      currentIndex === idx
                        ? "ring-2 ring-offset-2 ring-blue-500"
                        : "bg-gray-100 "
                    }`}
                    title={`Q ${idx + 1}`}
                  >
                    {idx + 1}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border rounded p-3">
            <div className="text-sm font-medium mb-2">Legend</div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-400 rounded"></div> Saved
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-yellow-400 rounded"></div> Marked
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-indigo-400 rounded"></div> Marked &
                Saved
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gray-200 rounded"></div> Not
                Attempted
              </div>
            </div>
          </div>

          <div className="mt-3 border rounded p-3">
            <div className="text-sm mb-2">Progress</div>
            <div>Total: {qCount}</div>
            <div>
              Attempted: {Object.values(answers).filter((a) => a.value).length}
            </div>
            <div>
              Marked: {Object.values(answers).filter((a) => a.marked).length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
